import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, signal, ViewChild } from '@angular/core';
import { MenuItem } from 'primeng/api';
import { WellExplorerService } from '../../services/well-explorer.service';
import { WellExplorerItem } from '../../models/well-explorer.model';
import { TreeNode } from 'primeng/api';
import { DialogService } from 'primeng/dynamicdialog';
import { Router } from '@angular/router';
import { StoreService, StorageKeys, SessionStorageKeys } from '../../services/store.service';
import { MediatorService } from 'src/app/shared/services/mediator.service';
import { ChangeSelectedTubular, OpenDesignInNewTab, WorkspaceCloned } from 'src/app/shared/models/mediator-messages.model';
import { Design } from 'src/app/shared/models/design.model';
import { getChildLevel } from './well-explorer-helpers';
import { lastValueFrom, Subscription } from 'rxjs';
import { User, UserRoles } from '../user-admin-page/user-model';
import { UsersService } from '../../services/users-service';
import { saveAs } from 'file-saver-es';
import { CurrentAppIdsDto } from '../../models/current-app-ids-dto';
import { PeriforOnChangeMessages, SignalRService } from 'src/app/shared/services/signal-r.service';
import { ContextMenu } from 'primeng/contextmenu';
import { DesignContextService } from '../../services/design-context.service';

@Component({
  selector: 'app-well-explorer',
  templateUrl: './well-explorer.component.html',
  styles: [`
      .well-explorer-cmp {
        display: block;
        height: calc(100vh - 45px);
        min-width: 260px;
        overflow: hidden;
      }

      :host ::ng-deep .p-treetable-scrollable-wrapper,
      :host ::ng-deep .p-treetable-scrollable-body,
      :host ::ng-deep .p-treetable-scrollable-header {
        overflow-x: hidden !important;
      }

      .selected-node {
        color: green; /* Change to your preferred highlight color */
        background-color: var(--p-toolbar-background);
      }
    `],
  providers: [DialogService],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class WellExplorerComponent implements OnInit, OnChanges, OnDestroy {
  private static readonly DEFAULT_WORKSPACE_TEMPLATE_ID = '00000000-0000-0000-0000-000000000000';
  private _wellExplorer: WellExplorerItem;
  private _subscriptions = new Subscription();
  private _currentUserDesign = false;
  readonly companyLevel: string = "companies";
  readonly designLevel: string = "designs";
  private _nodeNameOtherThanDesign: string;

  public design: Design;
  public treeLevelId: string;
  public treeItems: TreeNode[];
  public selectedNode: TreeNode;
  public contextMenuItems: MenuItem[];
  
  // Signals for reactive state (Angular 21 zoneless compatibility)
  public loading = signal<boolean>(false);
  public cols: any = [{ field: 'label', header: 'Label' }];
  public appIds: CurrentAppIdsDto;
  public allDesigns: Design[] = [];
  public recentDesignSelection: Design | null = null;
  public userRoles: UserRoles;
  public scrollHeight = signal<string>('calc(100vh - 85px)');
  public user: User;
  public selectedDesignId: string;

  @Output()
  public closed: EventEmitter<string>;

  @Input() public onScrollToNode: boolean;

  @ViewChild('treeTable') treeTable;
  @ViewChild('cm') contextMenu!: ContextMenu;

  constructor(
    private _usersService: UsersService,
    private _wellExplorerService: WellExplorerService,
    private _router: Router,
    private _store: StoreService,
    private _designContextService: DesignContextService,
    private _messenger: MediatorService,
    private _signalRService: SignalRService,
    private _cdr: ChangeDetectorRef,
  ) {
    this.closed = new EventEmitter();

    this._subscriptions.add(
      this._messenger.of(OpenDesignInNewTab).subscribe((treeNode) => {
        this.handleOpenDesignInNewTab(treeNode.treeNode);
      })
    );
  }

  async ngOnInit() {
    try {
      this.loading.set(true);
      const appIds = await lastValueFrom(this._usersService.getCurrentAppIds());
      await this.initializeWellExplorer(appIds);

      const signalRHub = this._signalRService.getConnectionToNotificationHub();
      signalRHub.on(SignalRService.ON_PFB_CHANGE, async (data: any) => {
        if (data.action === PeriforOnChangeMessages.REFRESH_WELL_EXPLORER) {
          const designId = this.design?.id;
          const treeLevelId = this.treeLevelId ? this.treeLevelId : (data.designId ?? designId);
          const currentUserDesign = data.designId ? designId === data.designId : true;

          if (!this._currentUserDesign && data.designId) {
            this._currentUserDesign = false;
            const appIds = await lastValueFrom(this._usersService.getCurrentAppIds());
            this.refreshWellExplorer(appIds.designId, true);
            return;
          }
        this.loading.set(true);

        this.refreshWellExplorer(treeLevelId, currentUserDesign);
        }
      });

      this.loading.set(false);
      this._cdr.markForCheck(); // Ensure change detection triggers after async operations
    } catch (err) {
      console.error("Failed during initialization:", err);
    } finally {
      this.loading.set(false);
      this._cdr.markForCheck();
    }
  }

  async initializeWellExplorer(appIds: CurrentAppIdsDto) {
    this.appIds = appIds;

    const urlSegments = this._router.url.split('/');
    const routeDesignId = urlSegments[2];

    this.userRoles = await this._store.get<UserRoles>(StorageKeys.ROLES);
    const storedUser = await this._store.get<User>(StorageKeys.USER);
    this.user = await lastValueFrom(this._usersService.getUser()).catch(() => storedUser);
    this._wellExplorer = await lastValueFrom(this._wellExplorerService.getWellExplorer());

    this.treeItems = this.buildTree(this._wellExplorer);
    const designToLoad = routeDesignId || this.appIds?.designId;
    this.selectedDesignId = designToLoad;
    this.allDesigns = this.findDesigns(this.treeItems);

    if (designToLoad) {
      if (!routeDesignId) {
        this._router.navigate(['/designs', this.appIds?.designId], { replaceUrl: true });
      }

      this.selectedNode = this.searchTree(null, this.treeItems, designToLoad);
      this.expandParents(this.selectedNode);

      const shouldSyncAppIdsFromRoute = !!routeDesignId && this.appIds?.designId !== routeDesignId;

      if (shouldSyncAppIdsFromRoute) {
        await this.nodeSelect({ node: this.selectedNode }, false);
      } else {
        await this.updateCurrentDesign(designToLoad, false);
      }
    }
  }

  ngOnChanges(): void {
    this.scrollToNode();
  }

  public refreshWellExplorer(treeNodeId: string, currentUserDesign: boolean): void {
    this._wellExplorerService.getWellExplorer().subscribe((wellExplorer: WellExplorerItem) => {

      this.treeItems = this.buildTree(wellExplorer);
      this.allDesigns = this.findDesigns(this.treeItems);
      this.allDesigns = this.findDesigns(this.treeItems);
      if (!currentUserDesign) {
        this.loading.set(false);
        return;
      }
      if (treeNodeId) {
        this.selectedNode = this.searchTree(null, this.treeItems, treeNodeId);
        this.selectedDesignId = treeNodeId;
        this.selectedDesignId = treeNodeId;
        this.expandParents(this.selectedNode);
      }
      setTimeout(() => {
        this.scrollToNode();
      }, 100);
      this.loading.set(false);
      this._cdr.markForCheck(); // Ensure change detection after async refresh
    });
  }

  public handleCloseSidenav(): void {
    this.closed.emit("close");
  }

  public async handleAddCompany(e: { levelData: { name: string, wellType?: any }, level: string, treeNode: TreeNode }): Promise<void> {
    await lastValueFrom(this._wellExplorerService.addCompany({
      parentId: e.treeNode?.data?.id,
      name: e.levelData.name,
      level: e.level
    }));
    this._nodeNameOtherThanDesign = e.levelData.name;
    this.refreshWellExplorer(e?.treeNode?.key, this._currentUserDesign)
  }

  public async handleAddChildNode(e: { levelData: { name: string, wellType?: any, designTemplate?: string,  organization?: string }, level: string, treeNode: TreeNode }): Promise<void> {
    if (e.level == this.designLevel) {
      const shouldApplyDefaultWorkspace = e.levelData.designTemplate === WellExplorerComponent.DEFAULT_WORKSPACE_TEMPLATE_ID;
      const createdDesignId = await lastValueFrom(this._wellExplorerService.addDesign({
        parentId: e.treeNode?.data?.id,
        name: e.levelData.name,
        level: e.level,
        wellType: e.levelData.wellType,
        dashboardDesignId: e.levelData.designTemplate ? e.levelData.designTemplate : null,
        organization: e.levelData.organization ? e.levelData.organization : null
      }));

      if (shouldApplyDefaultWorkspace) {
        this._messenger.publish(new WorkspaceCloned(createdDesignId));
      }

      this.updateTreeWithNewDesign(e.levelData.name, e.level, createdDesignId, e.treeNode);

      setTimeout(() => {
        this.selectedNode = this.searchTree(null, this.treeItems, createdDesignId);
        this.selectedDesignId = createdDesignId;
        this.scrollToNode();
      }, 100);
    } else {
      this.treeLevelId = await lastValueFrom(this._wellExplorerService.createTreeLevel({
        parentId: e.treeNode?.data?.id,
        name: e.levelData.name,
        level: e.level
      }));
    }
  }

  public async handleDeleteNode(treeNode: TreeNode): Promise<void> {
    this.loading.set(true);
    const level = treeNode.data.level;
    const levelId = treeNode?.data.id;

    const deleted = await lastValueFrom(this._wellExplorerService.deleteTreeLevel(levelId));

    if (deleted) {
      if (level !== this.companyLevel) {
        treeNode.parent.children = treeNode?.parent?.children?.filter(tnc => tnc.data.id != levelId);
        this.treeItems = [...this.treeItems];
      } else {
        const treeItems = this.treeItems.filter(tn => tn.data.id != levelId);
        this.treeItems = [...treeItems];
      }

      if (level === this.designLevel && this.design?.id === levelId) {
        this.selectedDesignId = null;
        await this.updateCurrentDesign(null, true);
      }

      this.allDesigns = this.findDesigns(this.treeItems);
      this.loading.set(false);
      this._cdr.markForCheck();
    }
  }

  public async handleRenameItem(e: { name: string, treeNode: TreeNode, isRename: boolean }): Promise<void> {
    if (e.name) {
      const renamed = await lastValueFrom(this._wellExplorerService
        .renameTreeLevel({
          id: e.treeNode.data.id,
          level: e.treeNode?.data?.level,
          name: e.name
        }));
      if (renamed) {
        e.treeNode.data.label = e.name;
        e.treeNode.parent.children.sort(this.alphaNumericSort);
        this.treeItems = [...this.treeItems];
      }
      if (this.appIds.designId == e.treeNode.data.id && e.treeNode.data.level == this.designLevel) {
        if (this.design) {
          this.design = { ...this.design, name: e.name };
        }
        this._store.setSession(SessionStorageKeys.DESIGN, { id: e.treeNode.data.id, name: e.name });
      }
      this.selectedNode = this.searchTree(null, this.treeItems, this.appIds.designId);
      this._cdr.markForCheck();
    }
  }

  public async handleCloneDesign(treeNode: TreeNode): Promise<void> {
    this.loading.set(true);
    try {
      const level = treeNode.data.level;
      if (level !== this.designLevel) {
        return;
      }

      const designId = treeNode.data.id;
      const wellboreId = treeNode.parent.data.id;
      const designName = treeNode.data.label;

      const createdLevelId = await lastValueFrom(this._wellExplorerService.cloneDesign(designId, wellboreId, designName));
      if (!createdLevelId) {
        return;
      }

      // Clone mutates the tree; force next read to bypass the short-lived cache.
      this._wellExplorerService.invalidateCache();

      // Switch the app context so all dependent views load the cloned design.
      await this.updateCurrentDesign(createdLevelId, true);

      this.selectedDesignId = createdLevelId;
      this.refreshWellExplorer(createdLevelId, true);
    } finally {
      this.loading.set(false);
      this._cdr.markForCheck();
    }
  }

  public async handleOpenDesignInNewTab(treeNode: TreeNode): Promise<void> {
    const data = treeNode?.data;
    const designId = data?.id;

    // Validate: must be a design node and not already open
    if (!designId || data.level !== this.designLevel) {
      return;
    }

    window.open(`/designs/${designId}`, '_blank');
  }

  public async nodeSelect(event: any, trackRecent = true): Promise<void> {
    if (event?.node?.data?.level === this.designLevel) {
      const previousDesign = this.design;
      this.selectedDesignId = event.node?.data.id;
      await this.updateCurrentDesign(event.node?.data.id, true);
      if (trackRecent && previousDesign?.id && previousDesign.id !== this.design?.id) {
        this.recentDesignSelection = previousDesign;
      }
    } else if (event?.data?.level === this.designLevel) {
      const previousDesign = this.design;
      await this.updateCurrentDesign(event.data.id, true);
      this.selectedDesignId = event.data.id;
      if (trackRecent && previousDesign?.id && previousDesign.id !== this.design?.id) {
        this.recentDesignSelection = previousDesign;
      }

      this.treeItems = this.buildTree(this._wellExplorer);
      this.selectedNode = this.searchTree(null, this.treeItems, event.data.id);
      this.expandParents(this.selectedNode);
    }

    this._messenger.publish(new ChangeSelectedTubular(null));
  }

  public handleContextMenuUpdated(contextMenuItems: MenuItem[]) {
    this.contextMenuItems = contextMenuItems;
  }

  public async handleExportDesign(treeNode: TreeNode) {
    if (treeNode.data.level !== this.designLevel) {
      return;
    }
    const designId = treeNode.data.id;
    const wellboreId = treeNode.parent.data.id;
    const designName = treeNode.data.label;

    const exportedDesign = await lastValueFrom(this._wellExplorerService.exportDesign(designId, wellboreId, designName));

    const bytes = new TextEncoder().encode(JSON.stringify(exportedDesign));
    const blob = new Blob([bytes], { type: "application/json;charset=utf-8" });
    const exportDate = new Date();
    const fileDate = `${exportDate.getFullYear()}-${String(exportDate.getMonth() + 1).padStart(2, '0')}-${String(exportDate.getDate()).padStart(2, '0')}`;
    saveAs(blob, `${exportedDesign.design.name}_${fileDate}.json`);
  }

  public async handleShowMoveDesignDialog(moveDesignForm: any): Promise<void> {
    this.loading.set(true);

    if (moveDesignForm != undefined) {
      const moved = await lastValueFrom(this._wellExplorerService.moveWellExplorerItem(moveDesignForm.itemId, moveDesignForm));
      if (moved) {
        this.refreshWellExplorer(moveDesignForm.itemId, true);
      }
    }

    this.loading.set(false);
    this._cdr.markForCheck();
  }

  public async handleShowMoveProjectDialog(moveProjectForm: any): Promise<void> {
    this.loading.set(true);

    if (moveProjectForm != undefined) {
      const moved = await lastValueFrom(this._wellExplorerService.moveWellExplorerItem(moveProjectForm.itemId, moveProjectForm));
      if (moved) {
        this.refreshWellExplorer(moveProjectForm.itemId, false);
      }
    }

    this.loading.set(false);
    this._cdr.markForCheck();
  }

  public async handleShowMoveSiteDialog(moveSiteForm: any): Promise<void> {
    this.loading.set(true);

    if (moveSiteForm != undefined) {
      const moved = await lastValueFrom(this._wellExplorerService.moveWellExplorerItem(moveSiteForm.itemId, moveSiteForm));
      if (moved) {
        this.refreshWellExplorer(moveSiteForm.itemId, false);
      }
    }

    this.loading.set(false);
    this._cdr.markForCheck();
  }

  public async handleShowMoveWellDialog(moveWellForm: any): Promise<void> {
    this.loading.set(true);

    if (moveWellForm != undefined) {
      const moved = await lastValueFrom(this._wellExplorerService.moveWellExplorerItem(moveWellForm.itemId, moveWellForm));
      if (moved) {
        this.refreshWellExplorer(moveWellForm.itemId, false);
      }
    }

    this.loading.set(false);
    this._cdr.markForCheck();
  }

  public async handleShowMoveWellboreDialog(moveWellboreForm: any): Promise<void> {
    this.loading.set(true);

    if (moveWellboreForm != undefined) {
      const moved = await lastValueFrom(this._wellExplorerService.moveWellExplorerItem(moveWellboreForm.itemId, moveWellboreForm));
      if (moved) {
        this.refreshWellExplorer(moveWellboreForm.itemId, false);
      }
    }

    this.loading.set(false);
    this._cdr.markForCheck();
  }

  public handleShowImportDesignDialog(treeNode: TreeNode): void {
    this.loading.set(true);
    const wellboreId: string = treeNode.data.id;

    const level = treeNode.data.level;
    if (level !== "wellbores") {
      this.loading.set(false);
      return;
    }

    const input = document.createElement("input");
    input.setAttribute("accept", ".json");
    input.setAttribute("type", "file");
    input.onchange = (e => this.onFileSelected(e, wellboreId, treeNode));
    input.click();
    this.loading.set(false);
  }

  private async updateTreeWithNewDesign(designName: string, level: any, createdLevelId: string, treeNode: TreeNode<any>) {
    const newTreeNode = {
      data: {
        label: designName,
        icon: this.setIcon(getChildLevel(level)),
        id: createdLevelId,
        level: this.designLevel
      }
    };

    const treeChildren: TreeNode[] = [...treeNode.children, newTreeNode];
    treeNode.children = treeChildren.sort(this.alphaNumericSort);
    this.treeItems = [...this.treeItems];

    await this.updateCurrentDesign(createdLevelId, true);;

    this.selectedNode = newTreeNode;
  }

  private buildTree(wellExplorerItem: WellExplorerItem): TreeNode[] {
    return wellExplorerItem?.children?.map((a: WellExplorerItem) => {
      return {
        data: {
          label: a.name,
          icon: this.setIcon(wellExplorerItem?.childLevel?.toLowerCase()),
          id: a.id,
          level: a.level,
          childLevel: a.childLevel
        },
        children: this.buildTree(a),
        expanded: false
      };
    });
  }

  public scrollToNode(): void {
    if (!this.treeTable) {
      return;
    }
    const rows = this.treeTable.scrollableViewChild?.scrollBodyViewChild?.nativeElement?.querySelectorAll('tr');
    const selectedNodeName = this._nodeNameOtherThanDesign ?? this.selectedNode?.data.label;
    let targetRow = null;
    rows.forEach((row: any) => {
      if (row.innerText == selectedNodeName) {
        targetRow = row;
      }
    });

    setTimeout(() => {
      this.treeTable.scrollTo({
        top: targetRow?.offsetTop - 200,
        behavior: 'smooth'
      });
      this._nodeNameOtherThanDesign = null;
    }, 100);
  }

  private expandParents(node: TreeNode): void {
    if (node && node.parent) {
      node.parent.expanded = true;
      this.expandParents(node.parent);
    }
    this.treeItems = [...this.treeItems];
  }

  private findDesigns(tree: TreeNode[]): Design[] {
    const designs: TreeNode[] = [];

    const traverse = (node: TreeNode, depth = 0) => {
      if (depth === 5) { // At the design level
        designs.push(node);
        return;
      }
      node.children?.forEach(child => traverse(child, depth + 1));
    };

    tree.forEach(company => traverse(company));

    return designs
      .sort(this.alphaNumericSort)
      .map(design => new Design({ name: design.data.label, id: design.data.id }));
  }

  onRightClick(event: MouseEvent): void {
    event.preventDefault();
    this.contextMenu.show(event);
  }

  private searchTree(parent: TreeNode, tree: TreeNode[], id: string): TreeNode {
    let foundNode: TreeNode<any>;
    for (const node of tree) {
      node.parent = parent;
      if (node.data.id === id) {
        foundNode = node;
      } else {
        if (node.children) {
          foundNode = this.searchTree(node, node.children, id);
        }
      }
      if (foundNode) {
        return foundNode;
      }
    }
  }

  private setIcon(menuLevel: string): string {
    switch (menuLevel) {
      case this.companyLevel:
        return '../../../../assets/icons/domain-24px.svg';
      case 'projects':
        return '../../../../assets/icons/assignment-24px.svg';
      case 'sites':
        return '../../../../assets/icons/place-24px.svg';
      case 'wells':
        return '../../../../assets/icons/oil-platform.svg';
      case 'wellbores':
        return '../../../../assets/icons/noun_drill bit_331639.svg';
      default:
        return '';
    }
  }

  private onFileSelected(event: any, wellboreId: string, treeNode: TreeNode) {
    const files = event.target.files;
    const jsonFile = files.item(0);

    const reader = new FileReader();
    reader.onload = async (e) => await this.onReaderLoad(e, wellboreId, treeNode);
    reader.readAsText(jsonFile);
  }

  private async onReaderLoad(event: any, wellboreId: string, treeNode: TreeNode): Promise<void> {
    this.loading.set(true);

    try {
      const designJsonObj = JSON.parse(event.target.result);
      const importedDesignId = await lastValueFrom(this._wellExplorerService.importDesign(wellboreId, designJsonObj));

      if (!importedDesignId) {
        return;
      }

      // Import mutates the tree; force next read to bypass the short-lived cache.
      this._wellExplorerService.invalidateCache();

      // Switch the app context first so dependent views reload against imported design.
      await this.updateCurrentDesign(importedDesignId, true);

      this.selectedDesignId = importedDesignId;
      this.refreshWellExplorer(importedDesignId, true);
    } finally {
      this.loading.set(false);
      this._cdr.markForCheck();
    }
  }

  private alphaNumericSort(a: TreeNode, b: TreeNode): number {
    return a.data.label.replace(" ", "").localeCompare(b.data.label.replace(" ", ""), undefined, {
      numeric: true,
      sensitivity: 'base'
    });
  }

  private async updateCurrentDesign(designId: string | null, updateAppIds: boolean): Promise<void> {
    const selectedNode = designId ? this.searchTree(null, this.treeItems, designId) : null;
    const fallbackName = selectedNode?.data?.label;

    this.design = await this._designContextService.setCurrentDesign(designId, {
      updateAppIds,
      fallbackName,
      onNavigate: (resolvedDesignId) => {
        const route = resolvedDesignId ? ['/designs', resolvedDesignId] : ['/designs'];
        this._router.navigate(route, { replaceUrl: true });
      }
    });

    const appIds = this._store.getSession<CurrentAppIdsDto>(SessionStorageKeys.APP_IDS);
    if (appIds) {
      this.appIds = appIds;
    }
  }

  ngOnDestroy(): void {
    this._subscriptions?.unsubscribe();
  }
}
